import {
  DateFormats,
  getInternationalDateFormatByLocale,
} from '@payaca/helpers/internationalHelper';
import {
  SupplierMaterialsCollectionFulfillment,
  SupplierMaterialsDeliveryFulfillment,
} from '@payaca/types/fulfillmentTypes';
import { differenceInDays, format, isToday, isTomorrow } from 'date-fns';
import { orderByDistance } from 'geolib';
import { omit } from 'lodash-es';
import { SupplierBranch } from '../gql/graphql';

export type DeliveryFulfillmentOption = SupplierMaterialsDeliveryFulfillment & {
  __typename: 'SupplierMaterialsDeliveryFulfillment';
};
export type CollectionFulfillmentOption =
  SupplierMaterialsCollectionFulfillment & {
    __typename: 'SupplierMaterialsCollectionFulfillment';
  };
export type FulfillmentOption =
  | DeliveryFulfillmentOption
  | CollectionFulfillmentOption;

export const isCollectionFulfillment = (f: FulfillmentOption) =>
  f.__typename === 'SupplierMaterialsCollectionFulfillment';

export const isDeliveryFulfillment = (f: FulfillmentOption) =>
  f.__typename === 'SupplierMaterialsDeliveryFulfillment';

export const getFulfillmentDate = (f: FulfillmentOption) => {
  return isCollectionFulfillment(f) ? f.collectionFromDate : f.deliveryFromDate;
};

export const getFromAvailabilityDate = (
  d: Date,
  isCollection: boolean,
  locale?: string
) => {
  const time = format(d, 'HH:mm');
  if (isToday(d)) {
    return `${isCollection ? 'from ' : ''}today${
      isCollection ? ` ${time}` : ''
    }`;
  } else if (isTomorrow(d)) {
    return `${isCollection ? 'from ' : ''}tomorrow${
      isCollection ? ` ${time}` : ''
    }`;
  } else {
    return `from ${format(
      d,
      getInternationalDateFormatByLocale(
        isCollection
          ? DateFormats.TIMESTAMP_DAY_MONTH
          : DateFormats.DAY_MONTH_SHORT,
        locale
      )
    )}`;
  }
};

export const getBadgeColour = (fulfillmentOption?: FulfillmentOption) => {
  const fulfillmentDays = fulfillmentOption
    ? differenceInDays(getFulfillmentDate(fulfillmentOption), new Date())
    : null;

  if (fulfillmentOption?.isFullFulfillment && fulfillmentDays !== null) {
    if (fulfillmentDays === 0) {
      return 'green';
    } else if (fulfillmentDays < 7) {
      return 'yellow';
    } else {
      return 'orange';
    }
  } else {
    if (fulfillmentOption) {
      return 'orange';
    }
    return 'red';
  }
};

export const sortBranches = (
  target: { latitude?: number; longitude?: number },
  branches?: SupplierBranch[] | null
) => {
  if (!target.latitude || !target.longitude) {
    return branches || [];
  }

  const coordBranches =
    branches
      ?.map((b) =>
        b.address.coordinates?.latitude && b.address.coordinates?.longitude
          ? {
              ...b,
              latitude: b.address.coordinates.latitude,
              longitude: b.address.coordinates.longitude,
            }
          : undefined
      )
      .filter((f) => f != undefined) || [];

  const sorted = orderByDistance(
    {
      latitude: target.latitude,
      longitude: target.longitude,
    },
    coordBranches
  );
  return sorted.map((s) => omit(s, 'latitude', 'longitude'));
};

/**
 * City plumbing returns branch names in format e.g. "WESTON SUPER MARE CPS", format to "Weston Super Mare CPS"
 */
export const readableBranchName = (name: string) => {
  const branchName = name
    .split(' ')
    .map((word) => {
      if (word === 'CPS') {
        return word;
      }
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ');
  return branchName;
};
